import { matchPath, useParams } from "react-router-dom"

import { AnalyticStatusConfig } from "app/AppRouter/AnalyticStatusConfig"
import { APP_STATUSES } from "utils/AppStatusContext"
import { isBrowser } from "utils/infrastructure/ssg-utils"

export function getCurrentStatus() {
    if (!isBrowser()) return
    const { searchParams } = new URL(location.href)
    const config = AnalyticStatusConfig.find(({ path }) =>
        matchPath({ path, end: true, caseSensitive: false }, location.pathname)
    )
    if (config) {
        if (typeof config.status === "function") {
            return config.status(searchParams) ?? APP_STATUSES.not_found_page
        }
        return config.status
    }
    return APP_STATUSES.not_found_page
}

interface SearchPagePathParams {
    filter: string
    page: string
}

export function useSearchPageParams() {
    return useParams<Partial<SearchPagePathParams>>()
}

function matchCurrentPath(path: string, options?: { end?: boolean; caseSensitive?: boolean }) {
    if (!isBrowser()) return false
    return !!matchPath({ path, ...options }, window.location.pathname)
}

function matchUrlPath(
    pathname: string,
    path: string,
    options?: { end?: boolean; caseSensitive?: boolean }
) {
    return !!matchPath({ path, ...options }, pathname)
}

export function isAuthPage() {
    return matchCurrentPath("/my/authorization")
}

export function isClientAccountPage() {
    return matchCurrentPath("/my/*")
}

export function isPsyAccountPage() {
    return matchCurrentPath("/psychologist-account/*")
}

export function isBookingPage() {
    return matchCurrentPath("/booking", { end: true })
}

export function isPaymentPage() {
    return matchCurrentPath(`/payment/:hash`, { end: true })
}

export function isSurveyPage() {
    return matchCurrentPath("/survey/psy", { end: true })
}

export function isSurveyResultsPage() {
    return matchCurrentPath("/results", { end: true })
}

export function isProfilePage() {
    return (
        matchCurrentPath("/psychologist/:id", { end: true }) ||
        matchCurrentPath("/psychologists/:filter/:slug", { end: true })
    )
}

export function isListPage() {
    return matchCurrentPath("/psychologist/list", { end: true })
}

export function isSearchPage() {
    return (
        matchCurrentPath("/psychologists", { end: true }) ||
        matchCurrentPath("/psychologists/:page", { end: true }) ||
        matchCurrentPath("/psychologists:filter(/[^/]+|)", { end: true }) ||
        matchCurrentPath("/psychologists:filter(/[^/]+|)/:page(\\d+)", { end: true })
    )
}

export function isClientAuthorizationPage() {
    return matchCurrentPath("/my/authorization", { end: true }) || isAuthByPromocodePage()
}

export function isPsychologistCollectionPage() {
    return matchCurrentPath("/psychologist/collection/:id", { end: true })
}

export function isBookingDetailsPage() {
    return matchCurrentPath("/booking/:id", { end: true })
}

export function isVideoLobbyPage() {
    return (
        matchCurrentPath("/my/call/lobby/*") ||
        matchCurrentPath("/psychologist-account/call/*") ||
        matchCurrentPath("/internal/call/*")
    )
}

export function isClientNotificationUrl(pathName: string) {
    return !(
        matchUrlPath(pathName, "/my/call/*") || matchUrlPath(pathName, "/psychologist-account/*")
    )
}

export function isRootPage() {
    return matchCurrentPath("/")
}

export function isHRAccountPage() {
    return matchCurrentPath("/my/statistics")
}

export function isAuthByPromocodePage() {
    return matchCurrentPath("/my/company/authorization")
}

export function isChat() {
    return matchCurrentPath("/chat")
}
